<template>
  <v-container
    id="previewInventory"
    fluid
    tag="section"
    class="full-height"
  >
    <base-v-component
      heading="Преглед на инвентаризация"
      goBackText="Обратно към инвентаризации"
      goBackTo="/items"
    />
    
    <v-row class="mt-10 d-print-none" v-if="!data.isWritten || isAdminUser">
      <v-col cols="12" class="text-right">
        <v-btn
          class="primary px-5"
          @click="writeToEPC"
        >
          <v-icon
            left
            dark
          >
            mdi-cellphone-nfc
          </v-icon>
          Запиши RFID
        </v-btn>

        <v-btn
          class="px-5"
          :color="zebraTagLocateState ? 'error' : 'success'"
          @click="locateTag"
          v-if="zebraIsConnected"
        >
          <v-icon
            left
            dark
          >
            mdi-nfc-search-variant
          </v-icon>
          {{ zebraTagLocateState ? 'Прекъсни търсене' : 'Намери артикула'}}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-10 d-print-none">
      <v-col cols="12" xl="7" lg="7" md="7">
        <base-material-card 
          hover-reveal
          icon="mdi-book-open-page-variant"
          color="primary"
          expandable
          expandableTitle="Детайли за артикул"
          :expanded.sync="expandItemDetails"
          :areAllCardsExpanded="areAllCardsExpanded"
          @collapseAllCards="collapseAllCards"
          @expandAllCards="expandAllCards"
        >
          <template v-slot:after-heading>
            <div class="font-weight-light card-title mt-2">
              Детайли за артикул
            </div>
          </template>

          <v-container class="py-7 pt-10">
            <v-form
              ref="form"
              lazy-validation
            >
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    label="Наименование"
                    v-model="data.name"
                    readonly
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    label="Автор"
                    v-model="data.author"
                    readonly
                  />
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    label="Година на публикуване"
                    v-model="data.publishedYear"
                    readonly
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    label="Инвентарен №"
                    v-model="data.inventoryNumber"
                    readonly
                  />
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    label="Сигнатура"
                    v-model="data.signature"
                    readonly
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    label="Дата"
                    v-model="itemDate"
                    readonly
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    label="Цена"
                    :value="data.price + ' лв.'"
                    readonly
                  />
                </v-col>
                <v-col cols="6" class="mx-auto">
                  <v-text-field
                    label="EPC"
                    :value="data.EPC"
                    readonly
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </base-material-card>
      </v-col>

      <v-col cols="12" xl="4" offset-xl="1" lg="5" md="5">
        <base-material-card 
          hover-reveal
          icon="mdi-cellphone-nfc"
          color="primary"
          expandable
          expandableTitle="Данни за RFID запис"
          :expanded.sync="expandWrittenDetails"
          :areAllCardsExpanded="areAllCardsExpanded"
          @collapseAllCards="collapseAllCards"
          @expandAllCards="expandAllCards"
        >
          <template v-slot:after-heading>
            <div class="font-weight-light card-title mt-2">
              Данни за RFID запис
            </div>
          </template>
          <v-container class="py-7">
            <v-card-text class="pt-0">
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td>Направен запис върху RFID:</td>
                      <td class="fw600 py-4 text-center">
                        <v-icon v-if="data.isWritten" color="success">mdi-check</v-icon>
                        <v-icon v-else color="error">mdi-close</v-icon>
                      </td>
                    </tr>
                    <tr>
                      <td>Записан на:</td>
                      <td class="fw600 py-5 text-center">{{writtenDate}}</td>
                    </tr>
                    <tr>
                      <td>Записан от:</td>
                      <td class="fw600 py-5 text-center">{{writtenBy}}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-container>
        </base-material-card>
      </v-col>
    </v-row>
    <v-row class="mt-10 d-print-none">
      <v-col cols="12" class="mx-auto">
        <base-material-card 
          hover-reveal
          icon="mdi-clipboard-check-multiple-outline"
          color="primary"
          expandable
          expandableTitle="Инвентаризации"
          :expanded.sync="expandInventariesDetails"
          :areAllCardsExpanded="areAllCardsExpanded"
          @collapseAllCards="collapseAllCards"
          @expandAllCards="expandAllCards"
        >
          <template v-slot:after-heading>
            <div class="font-weight-light card-title mt-2">
              Инвентаризации на артикула
            </div>
          </template>

          <v-container class="py-7 pt-10">
            <base-data-table 
              :headers="table.headers" 
              :pagination="table.pagination" 
              :data="table.data" 
              :sortBy.sync="table.sortBy"
              :sortDesc.sync="table.sortDesc" 
              @getData="getInventories" 
              @doAction="doAction"
            />
          </v-container>
        </base-material-card>
      </v-col>
    </v-row>
    <locate-tag-modal ref="locateTagModal" />
  </v-container>
</template>

<script>

import { mapGetters } from "vuex";
import { ISODateString } from '@/utils';
import { getItem, writeItemEPC } from '@/api/items';
import moment from 'moment';
import { getInventoriesByItem } from '@/api/inventories';
import { locateTagModal } from '@/components'
export default {
  name: "previewItem",
  components: {
    locateTagModal
  },
  data(){
    return {
      rawData: {},
      data: {
        name: '',
        date: '',
        createdBy: ''
      },
      hasTabsData: {},
      table: {
        headers: [
          { text: 'Дата', value: 'date', valueType: "date" },
          { text: 'Наименование', value: 'name', valueType: "text" },
          { text: 'Общо артикули', value: 'countImported', valueType: "text" },
          { text: 'Брой намерени артикули', value: 'countFound', valueType: "text" },
          { text: 'Брой липсващи артикули', value: 'countMissing', valueType: "text" },
          {
            sortable: false,
            text: '',
            value: '',
            valueType: 'button',
            style: "width: 50px;",
            class: 'text-right',
            buttons: [
              {
                label: 'Преглед',
                title: 'Преглед',
                icon: 'mdi-text-box-search-outline',
                size: 'small',
                class: 'primary',
                click: 'previewInventory',
              }
            ]
          },
        ],
        data: [],
        pagination: {
          itemsPerPage: 10,
          page: 1,
          perPageOptions: [5, 10, 25, 50, 100],
          total: 0
        },
        sortBy: 'date',
        sortDesc: true,
      },
      expandItemDetails: true,
      expandWrittenDetails: true,
      expandInventariesDetails: true
    }
  },
  computed: {
    ...mapGetters([
      'isAdminUser',
      'lastRoute',
      'user',
      'zebraIsConnected',
      'zebraTagLocateState'
    ]),
    areAllCardsExpanded(){
      return this.expandItemDetails && this.expandWrittenDetails && this.expandInventariesDetails;
    },
    itemDate() {
      return moment(this.data.date).format("DD.MM.YYYY");
    },
    writtenDate(){
      return this.data.isWritten ? moment(this.data.writtenDate).format("DD.MM.YYYY") : ' — '
    },
    writtenBy(){
      return this.data.isWritten ? this.data.writtenBy.fullName : ' — '
    }
  },
  mounted(){
    if(this.$route.params.id && this.$route.params.id.length == 24){
      this.data._id = this.$route.params.id;
      this.getData();
    }
  },
  methods: {
    getData(){
      if(this.data._id && this.data._id.length == 24){
        getItem(this.data._id).then(response => {
          if(response){
            this.data = response;
            this.getInventories();
          } else {
            this.$snotify.error("Документа не е намерен")
            this.generateNewDoc()
          }
        })
      }
    },
    getInventories(){
      let query = {};
      if(!query.page) query.page = this.table.pagination.page;
      query.itemsPerPage = this.table.pagination.itemsPerPage;
      query.sortBy = this.table.sortBy;
      query.sortDesc = this.table.sortDesc;
      getInventoriesByItem(this.data._id, query).then(response => {
        this.table.data = response.Items;
        this.table.pagination.total = response.Total;
        this.table.pagination.page = response.page;
      })
    },
    writeToEPC(){
      writeItemEPC({itemId: this.data._id}).then(response => {
        if(response)
          this.getData();
      })
    },
    locateTag(){
      this.$refs.locateTagModal.openModal(this.data);
    },
    expandAllCards(){
      this.expandGeneralDetails = true;
      this.expandSummarizedDetails = true;
      this.$forceUpdate();
    },
    collapseAllCards(){
      this.expandGeneralDetails = false;
      this.expandSummarizedDetails = false;
      this.$forceUpdate();
    },
    doAction(actionData) {
      switch(actionData.action){
        case 'previewInventory':
          this.$router.push({path: `/inventories/${actionData.item._id}`})
        break;
      }
    },
    ISODateString: ISODateString
  }
}
</script>